import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { useChatSidebarStore } from '@air/feature-ag-ui-chat';

import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { type CanvasViewedFrom } from '~/components/v2/store/shared/createWorkspaceRightPanelCanvasSlice';
import { useDiscardDraftCanvas } from '~/hooks/canvases/useDiscardDraftCanvas';
import { useRecordCanvasView } from '~/hooks/canvases/useRecordCanvasView';
import { getPrivateCanvasPageHref, type PrivateCanvasBoardHrefParams } from '~/utils/PathUtils';

type SelectCanvasOptions = {
  /** Opening board. Nested `/b/{boardId}/canvas/{id}` uses this, not canvas membership. */
  boardInfo?: PrivateCanvasBoardHrefParams;
  viewedFrom?: CanvasViewedFrom;
};

/**
 * Opens a canvas on its dedicated page and clears selection when that canvas is deleted.
 * The right panel no longer hosts a canvas.
 */
export const useSelectedCanvasId = () => {
  const { push } = useRouter();

  const selectedCanvasId = useWorkspaceRightPanelStore((state) => state.selectedCanvasId);
  const setSelectedCanvasId = useWorkspaceRightPanelStore((state) => state.setSelectedCanvasId);
  const setOpen = useWorkspaceRightPanelStore((state) => state.setOpen);
  const setFullscreen = useWorkspaceRightPanelStore((state) => state.setFullscreen);
  const setBoardViewCollapsed = useWorkspaceRightPanelStore((state) => state.setBoardViewCollapsed);

  const { recordView } = useRecordCanvasView();
  const discardDraftCanvas = useDiscardDraftCanvas();

  const selectCanvas = useCallback(
    (canvasId: string, options?: SelectCanvasOptions) => {
      useWorkspaceRightPanelStore.getState().setCanvasReturnTo(null);
      // Full-page open does not go through closeCanvas, so abandon any in-flight draft here.
      discardDraftCanvas();
      // Store viewedFrom for canvas_viewed. The page reads the id from the URL.
      setSelectedCanvasId(canvasId, options?.viewedFrom);
      void push(getPrivateCanvasPageHref(canvasId, options?.boardInfo));
      useChatSidebarStore.getState().setOpen(false);
      recordView(canvasId);
    },
    [discardDraftCanvas, push, recordView, setSelectedCanvasId],
  );

  const closeCanvas = useCallback(() => {
    discardDraftCanvas();
    // Persisted panel chrome from older sessions still collapses the workspace nav.
    setOpen(false);
    setSelectedCanvasId(null);
    setFullscreen(false);
    setBoardViewCollapsed(false);
  }, [discardDraftCanvas, setBoardViewCollapsed, setFullscreen, setOpen, setSelectedCanvasId]);

  return {
    selectedCanvasId,
    selectCanvas,
    closeCanvas,
  };
};
